import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { EventParticipant } from "@models/event-participants";
import { EventOrderFilters } from "@models/event-order-filters";
import { DateTime } from "luxon";
import { DatePickerModule } from "primeng/datepicker";
import { PaginatorState } from "primeng/paginator";
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  EMPTY,
  finalize,
  of,
  shareReplay,
  switchMap,
  tap,
} from "rxjs";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { LoadingComponent } from "../../../../shared/components/loading/loading.component";
import { getParams } from "../../../../utils/utils";
import { EventsManagementService } from "../../../services/events-management.service";
import { OrdersService } from "../../../services/orders.service";
import {
  OrderDetailsDialogComponent,
  OrderDetailsDialogData,
} from "../../../shared/modals/order-details-dialog/order-details-dialog.component";
import {
  ParticipantsSortField,
  ParticipantsTableComponent,
} from "./participants-table/participants-table.component";

type OrderStatusFilter = "paid" | "courtesy" | "refunded" | "";
const EMPTY_FILTERS = {
  search: "",
  status: "" as OrderStatusFilter,
  sales_channel: "",
  payment_method: "",
  paid_from: null as Date | null,
  paid_until: null as Date | null,
};
interface OrdersQuery {
  page: PaginatorState | null;
  filters: EventOrderFilters & { status?: OrderStatusFilter };
  sort: ParticipantsSortField;
  direction: "asc" | "desc";
}

@Component({
  selector: "app-participants-tab",
  imports: [
    CommonModule,
    ParticipantsTableComponent,
    ReactiveFormsModule,
    DatePickerModule,
    LoadingComponent,
    ButtonComponent,
  ],
  templateUrl: "./participants-tab.component.html",
  styleUrl: "./participants-tab.component.scss",
})
export class ParticipantsTabComponent {
  readonly filtersForm = new FormGroup({
    search: new FormControl("", { nonNullable: true }),
    status: new FormControl<OrderStatusFilter>("", { nonNullable: true }),
    sales_channel: new FormControl("", { nonNullable: true }),
    payment_method: new FormControl("", { nonNullable: true }),
    paid_from: new FormControl<Date | null>(null),
    paid_until: new FormControl<Date | null>(null),
  });
  readonly error = signal("");
  readonly validationError = signal("");
  readonly ordersLoading = signal(false);

  private readonly _eventsService = inject(EventsManagementService);
  private readonly _dialog = inject(MatDialog);
  private readonly _ordersService = inject(OrdersService);
  private readonly _route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  private readonly query$ = new BehaviorSubject<OrdersQuery>({
    page: null,
    filters: {},
    sort: "date_time_order",
    direction: "desc",
  });

  sort: ParticipantsSortField | null = "date_time_order";
  direction: "asc" | "desc" = "desc";

  eventParticipants$ = getParams("id").pipe(
    distinctUntilChanged(),
    switchMap((eventId) => {
      this.filtersForm.reset(EMPTY_FILTERS);
      this.validationError.set("");
      this.sort = "date_time_order";
      this.direction = "desc";
      this.query$.next({
        page: null,
        filters: {},
        sort: this.sort,
        direction: this.direction,
      });
      return this.query$.pipe(
        switchMap((query) => {
          this.error.set("");
          this.ordersLoading.set(true);
          return this._eventsService
            .getEventParticipants(eventId, query.page, undefined, {
              ...query.filters,
              sort: query.sort,
              direction: query.direction,
            })
            .pipe(
              catchError(() => {
                this.error.set(
                  "Não foi possível carregar os pedidos. Tente aplicar os filtros novamente.",
                );
                return of(null);
              }),
              finalize(() => this.ordersLoading.set(false)),
            );
        }),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  get first() {
    return this.query$.value.page?.first ?? 0;
  }
  get rows() {
    return this.query$.value.page?.rows ?? 10;
  }

  applyFilters() {
    const filters = this.filtersForm.getRawValue();
    const first = filters.paid_from
      ? DateTime.fromJSDate(filters.paid_from)
      : null;
    const last = filters.paid_until
      ? DateTime.fromJSDate(filters.paid_until)
      : null;
    if (
      (first && !first.isValid) ||
      (last && !last.isValid) ||
      (first && last && first.startOf("day") > last.startOf("day"))
    ) {
      this.validationError.set(
        "Informe um período válido, com data final igual ou posterior à inicial.",
      );
      return;
    }
    this.validationError.set("");
    this.query$.next({
      ...this.query$.value,
      page: { page: 0, first: 0, rows: this.rows },
      filters: {
        ...filters,
        paid_from: first?.toISODate() ?? null,
        paid_until: last?.toISODate() ?? null,
        search: filters.search.trim(),
        time_zone: DateTime.local().zoneName,
      },
    });
  }

  clearFilters() {
    this.filtersForm.reset(EMPTY_FILTERS);
    this.applyFilters();
  }

  loading = false;

  handleDetailOrder(participant: EventParticipant) {
    this.loading = true;
    this._ordersService
      .getOrderById(participant.order_number)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
        tap((order) => {
          this._dialog.open(OrderDetailsDialogComponent, {
            width: "800px",
            maxWidth: "95vw",
            data: { order } as OrderDetailsDialogData,
          });
        }),
        catchError(() => {
          this.error.set("Não foi possível carregar o detalhe do pedido.");
          return EMPTY;
        }),
      )
      .subscribe();
  }

  handlePageChange(page: PaginatorState) {
    this.query$.next({ ...this.query$.value, page });
  }

  resetToFirstPage() {
    this.query$.next({
      ...this.query$.value,
      page: { page: 0, first: 0, rows: this.rows },
    });
  }

  handleSort(field: ParticipantsSortField) {
    if (this.sort === field) {
      this.direction = this.direction === "asc" ? "desc" : "asc";
    } else {
      this.sort = field;
      this.direction = "desc";
    }
    this.query$.next({
      ...this.query$.value,
      page: { page: 0, first: 0, rows: this.rows },
      sort: this.sort!,
      direction: this.direction,
    });
  }

  downloadReport() {
    const eventId = this._route.snapshot.params["id"];
    if (!eventId) return;
    this.loading = true;
    this._eventsService
      .downloadOrdersReport(eventId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
        tap((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "relatorio_pedidos.pdf";
          a.click();
          URL.revokeObjectURL(url);
        }),
        catchError(() => {
          this.error.set("Não foi possível baixar o relatório completo.");
          return EMPTY;
        }),
      )
      .subscribe();
  }
}
